/**
 * Portable CSV mapping-profile files: a versioned JSON envelope around one or
 * a bounded collection of profiles, for reader-initiated export/import
 * between browsers and devices.
 *
 * This module is a strict *file* front end on the storage schema in
 * `@/lib/csv-mapping-profiles` — every member goes through
 * {@link parseMappingProfile} and the profile bounds, never a second schema.
 * It differs from the storage list parser in one deliberate way: storage
 * recovers by silently dropping bad entries and unknown keys (a reader's own
 * stale store must keep loading), while a file the reader chose to import is
 * **all-or-nothing**. Anything the storage parser would discard or normalize
 * away — an unknown or forbidden key, a mapping name outside the saved
 * headers, a category assignment it would drop — refuses the whole file with
 * typed issues the UI can show. A refused file changes nothing, and even an
 * accepted one only yields data: {@link previewCsvProfileImport} proposes
 * actions and never writes storage or active converter state.
 *
 * What a file can carry is exactly profile configuration: header names, the
 * name-keyed mapping, accounts, category assignments and description rules.
 * Rows, split allocations, row indexes, source keys, amounts, balances,
 * statement dates, CSV text and emitted ledger text have no field to land in
 * and are reported as `forbidden-field` when present. Sign rules are not
 * profile data either — they stay in the preset registry, joined back by
 * `presetSlug` at apply time through `checkProfileCompatibility`.
 *
 * Pure and dependency-free apart from sibling `src/lib` modules: no DOM, no
 * storage, no network.
 */

import { decodeUtf8Strict } from "@/lib/local-file";
import {
  CSV_DESCRIPTION_RULE_KEYS,
  CSV_PROFILE_CONFIG_KEYS,
  CSV_PROFILE_FIELDS,
  CSV_PROFILE_FORBIDDEN_CONFIG_KEYS,
  CSV_PROFILE_KEYS,
  CSV_PROFILE_VERSION,
  isRecord,
  MAX_CSV_PROFILES,
  MAX_CSV_PROFILE_JSON_BYTES,
  parseMappingProfile,
  utf8ByteLength,
  type CsvMappingProfile,
} from "@/lib/csv-mapping-profiles";

/** Envelope discriminator: identifies the file's kind, never its contents. */
export const CSV_PROFILE_FILE_FORMAT = "beancount.io/csv-mapping-profiles";
/** The only envelope version this code reads and writes. */
export const CSV_PROFILE_FILE_VERSION = 1;
/**
 * Largest file accepted, in UTF-8 bytes: a full collection at the
 * per-profile bound plus envelope/indentation slack. Derived from the
 * existing bounds so it cannot be raised independently of them.
 */
export const MAX_CSV_PROFILE_FILE_BYTES =
  MAX_CSV_PROFILES * MAX_CSV_PROFILE_JSON_BYTES + 4096;
/** Most issues one refusal reports; the file is refused either way. */
export const MAX_CSV_PROFILE_FILE_ISSUES = 20;
/** Media type for the download Blob and the file chooser's `accept`. */
export const CSV_PROFILE_FILE_MEDIA_TYPE = "application/json";
/** Fixed, content-free download names (no labels, dates or accounts). */
export const CSV_PROFILE_FILE_NAME_SINGLE = "beancount-csv-profile.json";
export const CSV_PROFILE_FILE_NAME_COLLECTION = "beancount-csv-profiles.json";

const ENVELOPE_KEYS = ["format", "fileVersion", "profiles"] as const;
const MAX_REPORTED_KEY_LENGTH = 64;

/**
 * Keys that name transaction or statement data. Checked at every object
 * level of the file except the mapping (whose `date`/`amount` keys name
 * converter fields) and the category-assignment record (keyed by the
 * reader's own category values). Extends — never replaces — the storage
 * contract's {@link CSV_PROFILE_FORBIDDEN_CONFIG_KEYS}.
 */
export const CSV_PROFILE_FILE_FORBIDDEN_KEYS: readonly string[] = [
  ...CSV_PROFILE_FORBIDDEN_CONFIG_KEYS,
  "rows",
  "rawRows",
  "records",
  "transactions",
  "entries",
  "csv",
  "csvText",
  "text",
  "sourceText",
  "input",
  "output",
  "outputText",
  "ledger",
  "ledgerText",
  "beancount",
  "amount",
  "amounts",
  "balance",
  "balances",
  "date",
  "dates",
  "statementDate",
  "splitAllocations",
];

/** Why a file (or one of its members) was refused. */
export type CsvProfileFileIssueReason =
  /** Over {@link MAX_CSV_PROFILE_FILE_BYTES}; checked before parsing. */
  | "too-large"
  /** Bytes are not valid UTF-8. */
  | "not-utf8"
  /** Not parseable JSON. */
  | "not-json"
  /** JSON, but not an object envelope with a `profiles` array. */
  | "not-envelope"
  /** `format` is not {@link CSV_PROFILE_FILE_FORMAT}. */
  | "unsupported-format"
  /** `fileVersion` is not {@link CSV_PROFILE_FILE_VERSION}. */
  | "unsupported-version"
  /** `profiles` is empty. */
  | "no-profiles"
  /** More than {@link MAX_CSV_PROFILES} members. */
  | "too-many-profiles"
  /** A key naming transaction/statement data (see the forbidden list). */
  | "forbidden-field"
  /** Any other key the schema does not define. */
  | "unknown-field"
  /** A member's schema `version` is not {@link CSV_PROFILE_VERSION}. */
  | "unsupported-profile-version"
  /** A member fails the profile validator or would lose data to it. */
  | "invalid-profile"
  /** A member serializes past {@link MAX_CSV_PROFILE_JSON_BYTES}. */
  | "profile-too-large"
  /** Two members share an id. */
  | "duplicate-id";

/**
 * One visible refusal reason. `profileIndex` is the zero-based member
 * position (`null` for envelope-level issues); `key` names the offending
 * key, truncated, for field issues. Never carries a value from the file.
 */
export interface CsvProfileFileIssue {
  reason: CsvProfileFileIssueReason;
  profileIndex: number | null;
  key?: string;
}

type CsvProfileFileParseResult =
  | { ok: true; profiles: CsvMappingProfile[] }
  | { ok: false; issues: CsvProfileFileIssue[] };

type CsvProfileFileExportResult =
  | {
      ok: true;
      text: string;
      fileName: string;
      mediaType: typeof CSV_PROFILE_FILE_MEDIA_TYPE;
      profileCount: number;
    }
  | { ok: false; issues: CsvProfileFileIssue[] };

const FORBIDDEN = new Set(CSV_PROFILE_FILE_FORBIDDEN_KEYS);

function reportedKey(key: string): string {
  return key.length > MAX_REPORTED_KEY_LENGTH
    ? `${key.slice(0, MAX_REPORTED_KEY_LENGTH)}…`
    : key;
}

/**
 * Report every key of `record` outside `allowed`, split into forbidden
 * (transaction/statement data) and merely unknown.
 */
function checkKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
  profileIndex: number | null,
  issues: CsvProfileFileIssue[],
  checkForbidden = true,
): void {
  for (const key of Object.keys(record)) {
    if (allowed.includes(key)) continue;
    issues.push({
      reason:
        checkForbidden && FORBIDDEN.has(key) ? "forbidden-field" : "unknown-field",
      profileIndex,
      key: reportedKey(key),
    });
  }
}

/**
 * Validate one file member strictly. Returns the parsed profile or pushes
 * issues and returns `null`. Strictness on top of {@link parseMappingProfile}
 * covers exactly what that parser would otherwise discard silently.
 */
function validateMember(
  raw: unknown,
  index: number,
  issues: CsvProfileFileIssue[],
): CsvMappingProfile | null {
  const before = issues.length;
  if (!isRecord(raw)) {
    issues.push({ reason: "invalid-profile", profileIndex: index });
    return null;
  }
  if (raw.version !== CSV_PROFILE_VERSION) {
    issues.push({ reason: "unsupported-profile-version", profileIndex: index });
    return null;
  }
  checkKeys(raw, CSV_PROFILE_KEYS, index, issues);
  if (isRecord(raw.mapping)) {
    // Mapping keys are converter field names (`date`, `amount`, …), so only
    // the unknown check applies here.
    checkKeys(raw.mapping, CSV_PROFILE_FIELDS, index, issues, false);
  }
  if (isRecord(raw.config)) {
    checkKeys(raw.config, CSV_PROFILE_CONFIG_KEYS, index, issues);
    if (Array.isArray(raw.config.descriptionRules)) {
      for (const rule of raw.config.descriptionRules) {
        if (isRecord(rule)) {
          checkKeys(rule, CSV_DESCRIPTION_RULE_KEYS, index, issues);
        }
      }
    }
  }
  const parsed = parseMappingProfile(raw);
  if (parsed === null) {
    issues.push({ reason: "invalid-profile", profileIndex: index });
    return null;
  }
  // Lossy normalization the storage parser tolerates but a file must not:
  // a mapped name outside the saved headers silently becomes unmapped, and
  // malformed/over-cap category assignments are silently dropped.
  const rawMapping = raw.mapping as Record<string, unknown>;
  const mappingLost = CSV_PROFILE_FIELDS.some(
    (field) =>
      rawMapping[field] !== null &&
      rawMapping[field] !== undefined &&
      parsed.mapping[field] === null,
  );
  const rawCategories = (raw.config as Record<string, unknown>).categoryAccounts;
  const categoriesLost =
    isRecord(rawCategories) &&
    Object.keys(rawCategories).length !==
      Object.keys(parsed.config.categoryAccounts ?? {}).length;
  if (mappingLost || categoriesLost) {
    issues.push({ reason: "invalid-profile", profileIndex: index });
  }
  if (utf8ByteLength(JSON.stringify(parsed)) > MAX_CSV_PROFILE_JSON_BYTES) {
    issues.push({ reason: "profile-too-large", profileIndex: index });
  }
  return issues.length === before ? parsed : null;
}

/**
 * Validate every member and refuse duplicate ids — the one loop import and
 * export share, so an export always yields a file import accepts back.
 */
function validateMembers(
  members: readonly unknown[],
  issues: CsvProfileFileIssue[],
): CsvMappingProfile[] {
  const profiles: CsvMappingProfile[] = [];
  const seenIds = new Set<string>();
  members.forEach((member, index) => {
    const parsed = validateMember(member, index, issues);
    if (parsed === null) return;
    if (seenIds.has(parsed.id)) {
      issues.push({ reason: "duplicate-id", profileIndex: index });
      return;
    }
    seenIds.add(parsed.id);
    profiles.push(parsed);
  });
  return profiles;
}

function refuse(issues: CsvProfileFileIssue[]): {
  ok: false;
  issues: CsvProfileFileIssue[];
} {
  return { ok: false, issues: issues.slice(0, MAX_CSV_PROFILE_FILE_ISSUES) };
}

/**
 * Validate a chosen file's contents. All-or-nothing: `ok: true` only when
 * every member is valid and nothing would be dropped; otherwise every issue
 * found (capped) with no profiles at all. Pure — never touches storage.
 */
export function parseCsvProfileFile(
  input: string | Uint8Array,
): CsvProfileFileParseResult {
  const byteLength =
    typeof input === "string" ? utf8ByteLength(input) : input.byteLength;
  if (byteLength > MAX_CSV_PROFILE_FILE_BYTES) {
    return refuse([{ reason: "too-large", profileIndex: null }]);
  }
  // Strips a leading BOM, which is legal in a UTF-8 file but not in JSON.
  const text = decodeUtf8Strict(input);
  if (text === null) return refuse([{ reason: "not-utf8", profileIndex: null }]);
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return refuse([{ reason: "not-json", profileIndex: null }]);
  }
  if (!isRecord(raw) || !Array.isArray(raw.profiles)) {
    return refuse([{ reason: "not-envelope", profileIndex: null }]);
  }
  if (raw.format !== CSV_PROFILE_FILE_FORMAT) {
    return refuse([{ reason: "unsupported-format", profileIndex: null }]);
  }
  if (raw.fileVersion !== CSV_PROFILE_FILE_VERSION) {
    return refuse([{ reason: "unsupported-version", profileIndex: null }]);
  }
  const issues: CsvProfileFileIssue[] = [];
  checkKeys(raw, ENVELOPE_KEYS, null, issues);
  const members = raw.profiles;
  if (members.length === 0) {
    issues.push({ reason: "no-profiles", profileIndex: null });
  } else if (members.length > MAX_CSV_PROFILES) {
    issues.push({ reason: "too-many-profiles", profileIndex: null });
    return refuse(issues);
  }
  const profiles = validateMembers(members, issues);
  return issues.length > 0 ? refuse(issues) : { ok: true, profiles };
}

/**
 * Pick only the schema's own fields — the export is rebuilt from the
 * allowlist, so widget state that rode along on an in-memory object (split
 * drafts, statement fields) is never serialized.
 */
function portableShape(profile: CsvMappingProfile): Record<string, unknown> {
  const pick = (
    source: object,
    keys: readonly string[],
  ): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const key of keys) {
      const value = (source as Record<string, unknown>)[key];
      if (value !== undefined) out[key] = value;
    }
    return out;
  };
  const shape = pick(profile, CSV_PROFILE_KEYS);
  const config = pick(profile.config ?? {}, CSV_PROFILE_CONFIG_KEYS);
  if (Array.isArray(config.descriptionRules)) {
    config.descriptionRules = config.descriptionRules.map((rule: object) =>
      pick(rule, CSV_DESCRIPTION_RULE_KEYS),
    );
  }
  shape.config = config;
  shape.mapping = pick(profile.mapping ?? {}, CSV_PROFILE_FIELDS);
  return shape;
}

/**
 * Build a portable file from validated profiles (a selected saved profile,
 * the whole saved collection, or a snapshot of the active configuration).
 * Every member passes the same strict validation import uses, so an export
 * that succeeds is always a file this code accepts back unchanged; an
 * invalid member refuses the export rather than being dropped.
 */
export function serializeCsvProfileFile(
  profiles: readonly CsvMappingProfile[],
): CsvProfileFileExportResult {
  if (profiles.length === 0) {
    return refuse([{ reason: "no-profiles", profileIndex: null }]);
  }
  if (profiles.length > MAX_CSV_PROFILES) {
    return refuse([{ reason: "too-many-profiles", profileIndex: null }]);
  }
  const issues: CsvProfileFileIssue[] = [];
  // JSON round trip first so the validator sees exactly what a file holds.
  const validated = validateMembers(
    profiles.map((profile): unknown =>
      JSON.parse(JSON.stringify(portableShape(profile))),
    ),
    issues,
  );
  if (issues.length > 0) return refuse(issues);
  const text = `${JSON.stringify(
    {
      format: CSV_PROFILE_FILE_FORMAT,
      fileVersion: CSV_PROFILE_FILE_VERSION,
      profiles: validated,
    },
    null,
    2,
  )}\n`;
  if (utf8ByteLength(text) > MAX_CSV_PROFILE_FILE_BYTES) {
    return refuse([{ reason: "too-large", profileIndex: null }]);
  }
  return {
    ok: true,
    text,
    fileName:
      validated.length === 1
        ? CSV_PROFILE_FILE_NAME_SINGLE
        : CSV_PROFILE_FILE_NAME_COLLECTION,
    mediaType: CSV_PROFILE_FILE_MEDIA_TYPE,
    profileCount: validated.length,
  };
}

/** What applying one imported profile to the saved collection would do. */
export type CsvProfileImportAction =
  /** Save as a new profile under its own (unused) id. */
  | "add"
  /** Leave the saved collection as it is for this member. */
  | "skip"
  /** Overwrite the conflicting saved profile — only ever an explicit choice. */
  | "replace"
  /** Save as a copy; the apply step allocates a fresh id. */
  | "keep-both";

/**
 * A saved profile the incoming one collides with: the same id, or (with a
 * different id) the same non-empty label, compared trimmed and
 * case-insensitively. `identical` means every configured field matches, so
 * importing it changes nothing.
 */
export type CsvProfileImportConflictKind = "same-id" | "same-label";

interface CsvProfileImportConflict {
  kind: CsvProfileImportConflictKind;
  existingId: string;
  identical: boolean;
}

export interface CsvProfileImportPreviewItem {
  /** Zero-based position in the file. */
  index: number;
  profile: CsvMappingProfile;
  conflict: CsvProfileImportConflict | null;
  /** The safe default: never `replace`. */
  proposedAction: CsvProfileImportAction;
  /** The choices the review UI may offer for this member. */
  allowedActions: readonly CsvProfileImportAction[];
}

interface CsvProfileImportPreview {
  items: CsvProfileImportPreviewItem[];
  /** Incoming ids that collide with nothing saved. */
  newIds: string[];
  conflictCount: number;
  /** Saved count after applying every proposed action. */
  proposedSavedCount: number;
  /** True when the proposed actions would exceed {@link MAX_CSV_PROFILES}. */
  overCapacity: boolean;
}

/** Content identity without the id, for "is this the same configuration". */
function contentKey(profile: CsvMappingProfile): string {
  return JSON.stringify(portableShape({ ...profile, id: "" }));
}

function labelKey(label: string): string {
  return label.trim().toLowerCase();
}

const NO_CONFLICT_ACTIONS: readonly CsvProfileImportAction[] = ["add", "skip"];
const CONFLICT_ACTIONS: readonly CsvProfileImportAction[] = [
  "skip",
  "replace",
  "keep-both",
];

/**
 * Saved-collection size once `actionFor` is applied to every item: `add` and
 * `keep-both` each add a profile, `skip` and `replace` add none. The single
 * definition behind both the proposed and the reader-chosen capacity check.
 */
export function savedCountAfter(
  savedCount: number,
  items: readonly CsvProfileImportPreviewItem[],
  actionFor: (item: CsvProfileImportPreviewItem) => CsvProfileImportAction,
): number {
  return (
    savedCount +
    items.filter((item) => {
      const action = actionFor(item);
      return action === "add" || action === "keep-both";
    }).length
  );
}

/**
 * Compare validated incoming profiles against the saved collection and
 * propose an action for each. Pure: returns new objects and never mutates
 * `incoming`, `saved` or any storage/active state — applying is a separate,
 * explicit step owned by the caller.
 */
export function previewCsvProfileImport(
  incoming: readonly CsvMappingProfile[],
  saved: readonly CsvMappingProfile[],
): CsvProfileImportPreview {
  const savedById = new Map(saved.map((profile) => [profile.id, profile]));
  const items: CsvProfileImportPreviewItem[] = incoming.map((profile, index) => {
    const byId = savedById.get(profile.id);
    const byLabel =
      byId === undefined && profile.label.trim() !== ""
        ? saved.find(
            (existing) => labelKey(existing.label) === labelKey(profile.label),
          )
        : undefined;
    const existing = byId ?? byLabel;
    if (existing === undefined) {
      return {
        index,
        profile,
        conflict: null,
        proposedAction: "add",
        allowedActions: NO_CONFLICT_ACTIONS,
      };
    }
    const identical = contentKey(existing) === contentKey(profile);
    return {
      index,
      profile,
      conflict: {
        kind: byId !== undefined ? "same-id" : "same-label",
        existingId: existing.id,
        identical,
      },
      proposedAction: identical ? "skip" : "keep-both",
      allowedActions: CONFLICT_ACTIONS,
    };
  });
  const proposedSavedCount = savedCountAfter(
    saved.length,
    items,
    (item) => item.proposedAction,
  );
  return {
    items,
    newIds: items
      .filter((item) => item.conflict === null)
      .map((item) => item.profile.id),
    conflictCount: items.filter((item) => item.conflict !== null).length,
    proposedSavedCount,
    overCapacity: proposedSavedCount > MAX_CSV_PROFILES,
  };
}
